import { doc, runTransaction, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { AuthenticatedUser } from '../domain/auth';
import { resolvePersonalBillingAccount } from '../firebase/functions';
import { logger } from './logger';

const PROVISIONING_VERSION = 1;
const sameStrings = (left: unknown, right: string[]) =>
  Array.isArray(left) &&
  left.length === right.length &&
  left.every((value, index) => value === right[index]);

export const accountProvisioningService = {
  async ensureUserProvisioned(
    user: AuthenticatedUser,
  ): Promise<AuthenticatedUser> {
    if (!user.emailVerified)
      throw new Error('Email verification is required before account setup.');
    if (
      !auth.currentUser ||
      auth.currentUser.uid !== user.uid ||
      !auth.currentUser.emailVerified
    ) {
      throw new Error('The verified account changed before setup completed.');
    }

    const providerIds = [...user.providerIds].sort();
    const userRef = doc(db, 'users', user.uid);
    let username: string | null = null;
    let usernameUpdatedAt: Date | null = null;

    await runTransaction(db, async (transaction) => {
      const userSnapshot = await transaction.get(userRef);

      if (!userSnapshot.exists()) {
        transaction.set(userRef, {
          email: user.email,
          createdAt: serverTimestamp(),
          initialProviderIds: providerIds,
          providerIds,
          provisioningVersion: PROVISIONING_VERSION,
        });
      } else {
        const current = userSnapshot.data();
        username = current.username || null;
        usernameUpdatedAt = current.usernameUpdatedAt?.toDate
          ? current.usernameUpdatedAt.toDate()
          : null;
        const updates: Record<string, string | number | string[] | null> = {};
        if (current.email !== user.email) updates.email = user.email;
        if (!sameStrings(current.providerIds, providerIds))
          updates.providerIds = providerIds;
        if (current.provisioningVersion !== PROVISIONING_VERSION) {
          updates.provisioningVersion = PROVISIONING_VERSION;
        }
        if (Object.keys(updates).length) transaction.update(userRef, updates);
      }
    });

    // Billing and collection creation can retry this idempotent setup if it fails.
    // Keep sign-in usable while billing initialization is pending or unavailable.
    if (auth.currentUser?.uid === user.uid && auth.currentUser.emailVerified) {
      void resolvePersonalBillingAccount({}).catch(() => {
        logger.warn('Billing account initialization failed.');
      });
    }

    return {
      ...user,
      username: user.username ?? username,
      usernameUpdatedAt: user.usernameUpdatedAt ?? usernameUpdatedAt,
    };
  },
};
